import { AdsProvider, InterstitialFailure } from './ads.provider';
import { environment } from '@env/environment';

declare global {
    interface Window {
        adsbygoogle: any[];
    }
}

export class AdsenseProvider implements AdsProvider {
    private static readonly INTERSTITIAL_CLOSE_DELAY_SECONDS = 5;
    private static readonly BANNER_PLACEHOLDER_HEIGHT_PX = 90;
    private static readonly INTERSTITIAL_PLACEHOLDER_SRC = '/assets/images/ad_interstitial_placeholder.png';
    private initialized = false;
    private currentBannerContainerId?: string;
    private currentBannerPlacementId?: string;
    private bannerResizeObserver?: ResizeObserver;
    private bannerStatusObserver?: MutationObserver;
    private currentBannerAdEl?: HTMLElement;
    private interstitialOverlayEl?: HTMLDivElement;
    private interstitialCountdownIntervalId?: number;
    private interstitialStatusObserver?: MutationObserver;
    private bannerPlaceholderDisplayedListener?: () => void;
    private bannerPlaceholderVisibilityListener?: (containerId: string, isVisible: boolean) => void;
    private interstitialPlaceholderDisplayedListener?: () => void;
    private interstitialPlaceholderClickListener?: () => void;
    private interstitialShownListener?: (placementId: string, provider: string) => void;
    private interstitialShowFailedListener?: (placementId: string, failure: InterstitialFailure) => void;
    private currentInterstitialPlacementId?: string;
    private forcePlaceholderAdsEnabled = false;

    async init(): Promise<void> {
        if (this.initialized) return;

        window.adsbygoogle = window.adsbygoogle || [];
        this.initialized = true;
    }

    async showBanner(placementId: string, containerId: string): Promise<void> {
        await this.init();
        if (this.currentBannerContainerId && this.currentBannerContainerId !== containerId) {
            this.notifyBannerPlaceholderVisibility(false, this.currentBannerContainerId);
        }
        this.currentBannerContainerId = containerId;
        this.currentBannerPlacementId = placementId;
        this.renderBanner(containerId, placementId);
    }

    async hideBanner(): Promise<void> {
        this.clearBannerContainer();
        this.notifyBannerPlaceholderVisibility(false);
        this.removeBannerLoadedStyles();
    }

    async destroyBanner(): Promise<void> {
        this.clearBannerContainer();
        this.notifyBannerPlaceholderVisibility(false);
        this.removeBannerLoadedStyles();
        this.currentBannerContainerId = undefined;
        this.currentBannerPlacementId = undefined;
    }

    setBannerPlaceholderDisplayedListener(listener?: () => void): void {
        this.bannerPlaceholderDisplayedListener = listener;
    }

    setBannerPlaceholderVisibilityListener(listener?: (containerId: string, isVisible: boolean) => void): void {
        this.bannerPlaceholderVisibilityListener = listener;
    }

    setInterstitialPlaceholderClickListener(listener?: () => void): void {
        this.interstitialPlaceholderClickListener = listener;
    }

    setInterstitialPlaceholderDisplayedListener(listener?: () => void): void {
        this.interstitialPlaceholderDisplayedListener = listener;
    }

    setInterstitialShownListener(listener?: (placementId: string, provider: string) => void): void {
        this.interstitialShownListener = listener;
    }

    setInterstitialShowFailedListener(
        listener?: (placementId: string, failure: InterstitialFailure) => void,
    ): void {
        this.interstitialShowFailedListener = listener;
    }

    setForcePlaceholderAdsEnabled(enabled: boolean): void {
        this.forcePlaceholderAdsEnabled = enabled;
    }

    async resumeBanner(): Promise<void> {
        if (this.currentBannerContainerId && this.currentBannerPlacementId) {
            this.renderBanner(this.currentBannerContainerId, this.currentBannerPlacementId);
        }
    }

    supportsInterstitial(): boolean {
        return true;
    }

    async preloadInterstitial(): Promise<void> {}

    async showInterstitial(placementId: string): Promise<void> {
        await this.init();
        if (!placementId) return;
        this.currentInterstitialPlacementId = placementId;

        this.removeInterstitialOverlay();

        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.inset = '0';
        overlay.style.zIndex = '99999';
        overlay.style.background = 'rgba(0, 0, 0, 0.75)';
        overlay.style.display = 'flex';
        overlay.style.alignItems = 'center';
        overlay.style.justifyContent = 'center';
        overlay.style.padding = '16px';

        const card = document.createElement('div');
        card.style.background = '#fff';
        card.style.borderRadius = '12px';
        card.style.width = 'min(92vw, 360px)';
        card.style.maxHeight = '88vh';
        card.style.overflow = 'hidden';
        card.style.padding = '12px';
        card.style.boxSizing = 'border-box';
        card.style.display = 'flex';
        card.style.flexDirection = 'column';
        card.style.gap = '12px';

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.textContent = `Close (${AdsenseProvider.INTERSTITIAL_CLOSE_DELAY_SECONDS})`;
        closeBtn.disabled = true;
        closeBtn.style.alignSelf = 'flex-end';
        closeBtn.style.border = '0';
        closeBtn.style.background = 'transparent';
        closeBtn.style.fontSize = '14px';
        closeBtn.style.cursor = 'not-allowed';
        closeBtn.style.opacity = '0.6';
        closeBtn.onclick = () => {
            if (closeBtn.disabled) return;
            this.removeInterstitialOverlay();
        };

        const adContainer = document.createElement('div');
        adContainer.style.width = '100%';
        adContainer.style.aspectRatio = '1 / 1';
        adContainer.style.minHeight = '280px';

        card.appendChild(closeBtn);
        card.appendChild(adContainer);
        overlay.appendChild(card);
        document.body.appendChild(overlay);
        this.interstitialOverlayEl = overlay;

        this.startInterstitialCloseCountdown(closeBtn);

        if (this.forcePlaceholderAdsEnabled) {
            this.showInterstitialPlaceholder(adContainer);
            return;
        }

        const adEl = this.createAdElement(placementId, false, 'rectangle');
        adEl.style.width = '100%';
        adEl.style.height = '100%';

        adContainer.appendChild(adEl);
        this.observeInterstitialStatus(adEl, adContainer, closeBtn);
        this.push();
    }

    supportsRewarded(): boolean {
        return false;
    }

    async preloadRewarded(): Promise<void> {}
    async showRewarded(): Promise<void> {}

    private push() {
        try {
            window.adsbygoogle.push({});
            return true;
        } catch {
            return false;
        }
    }

    private renderBanner(containerId: string, placementId: string) {
        const container = document.getElementById(containerId);
        if (!container) return;

        this.bannerResizeObserver?.disconnect();
        this.bannerResizeObserver = undefined;
        this.bannerStatusObserver?.disconnect();
        this.bannerStatusObserver = undefined;
        this.currentBannerAdEl = undefined;
        container.innerHTML = '';
        this.notifyBannerPlaceholderVisibility(false, containerId);

        if (this.forcePlaceholderAdsEnabled) {
            this.showBannerPlaceholder();
            return;
        }

        const adEl = this.createAdElement(placementId, true);
        this.currentBannerAdEl = adEl;
        container.appendChild(adEl);

        this.observeBannerStatus(adEl);
        this.observeBannerSize(adEl);
        this.push();
        this.applyBannerLoadedStyles(0);
    }

    private clearBannerContainer() {
        this.bannerResizeObserver?.disconnect();
        this.bannerResizeObserver = undefined;
        this.bannerStatusObserver?.disconnect();
        this.bannerStatusObserver = undefined;
        this.currentBannerAdEl = undefined;
        if (!this.currentBannerContainerId) return;
        const container = document.getElementById(this.currentBannerContainerId);
        if (container) {
            container.innerHTML = '';
        }
    }

    private startInterstitialCloseCountdown(closeBtn: HTMLButtonElement) {
        if (this.interstitialCountdownIntervalId) {
            window.clearInterval(this.interstitialCountdownIntervalId);
            this.interstitialCountdownIntervalId = undefined;
        }

        let secondsLeft = AdsenseProvider.INTERSTITIAL_CLOSE_DELAY_SECONDS;
        this.interstitialCountdownIntervalId = window.setInterval(() => {
            secondsLeft -= 1;
            if (secondsLeft > 0) {
                closeBtn.textContent = `Close (${secondsLeft})`;
                return;
            }

            closeBtn.textContent = 'Close';
            closeBtn.disabled = false;
            closeBtn.style.cursor = 'pointer';
            closeBtn.style.opacity = '1';
            if (this.interstitialCountdownIntervalId) {
                window.clearInterval(this.interstitialCountdownIntervalId);
                this.interstitialCountdownIntervalId = undefined;
            }
        }, 1000);
    }

    private removeInterstitialOverlay() {
        this.interstitialStatusObserver?.disconnect();
        this.interstitialStatusObserver = undefined;
        if (this.interstitialCountdownIntervalId) {
            window.clearInterval(this.interstitialCountdownIntervalId);
            this.interstitialCountdownIntervalId = undefined;
        }

        if (this.interstitialOverlayEl?.parentElement) {
            this.interstitialOverlayEl.parentElement.removeChild(this.interstitialOverlayEl);
        }
        this.interstitialOverlayEl = undefined;
    }

    private createAdElement(placementId: string, responsive: boolean, format = 'horizontal'): HTMLElement {
        const adEl = document.createElement('ins');
        adEl.className = 'adsbygoogle';
        adEl.style.display = 'block';
        adEl.setAttribute('data-ad-client', environment.adsenseConfig?.appId || '');
        adEl.setAttribute('data-ad-slot', placementId);
        adEl.setAttribute('data-ad-format', format);
        adEl.setAttribute('data-full-width-responsive', responsive ? 'true' : 'false');
        return adEl;
    }

    private observeBannerStatus(adEl: HTMLElement) {
        this.bannerStatusObserver = new MutationObserver(() => {
            const status = adEl.getAttribute('data-ad-status');
            if (status === 'unfilled') {
                this.showBannerPlaceholder();
            } else if (status === 'filled') {
                this.hideBannerPlaceholder();
                this.updateBannerDimensions(adEl);
            }
        });

        this.bannerStatusObserver.observe(adEl, {
            attributes: true,
            attributeFilter: ['data-ad-status'],
        });
    }

    private observeInterstitialStatus(adEl: HTMLElement, adContainer: HTMLDivElement, closeBtn: HTMLButtonElement) {
        this.interstitialStatusObserver?.disconnect();
        this.interstitialStatusObserver = new MutationObserver(() => {
            const status = adEl.getAttribute('data-ad-status');
            if (status === 'unfilled') {
                this.showInterstitialPlaceholder(adContainer);
                if (this.currentInterstitialPlacementId) {
                    this.interstitialShowFailedListener?.(this.currentInterstitialPlacementId, {
                        failureReason: 'noInventory',
                        placeholderShown: true,
                    });
                }
            } else if (status === 'filled') {
                closeBtn.style.visibility = 'visible';
                if (this.currentInterstitialPlacementId) {
                    this.interstitialShownListener?.(this.currentInterstitialPlacementId, 'adsense');
                }
            }
        });

        this.interstitialStatusObserver.observe(adEl, {
            attributes: true,
            attributeFilter: ['data-ad-status'],
        });
    }

    private showInterstitialPlaceholder(adContainer: HTMLDivElement) {
        this.interstitialPlaceholderDisplayedListener?.();
        adContainer.innerHTML = '';
        adContainer.style.aspectRatio = '2 / 3';
        adContainer.style.minHeight = '0';

        const placeholderCta = document.createElement('div');
        placeholderCta.style.width = '100%';
        placeholderCta.style.height = 'auto';
        placeholderCta.style.cursor = 'pointer';
        placeholderCta.onclick = () => {
            this.removeInterstitialOverlay();
            this.interstitialPlaceholderClickListener?.();
        };

        const placeholderImage = document.createElement('img');
        placeholderImage.src = AdsenseProvider.INTERSTITIAL_PLACEHOLDER_SRC;
        placeholderImage.alt = 'Upgrade to remove ads';
        placeholderImage.style.display = 'block';
        placeholderImage.style.width = '100%';
        placeholderImage.style.height = 'auto';
        placeholderImage.style.borderRadius = '12px';

        placeholderCta.appendChild(placeholderImage);
        adContainer.appendChild(placeholderCta);
    }

    private observeBannerSize(adEl: HTMLElement) {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.bannerResizeObserver = new ResizeObserver(() => {
            this.updateBannerDimensions(adEl);
        });
        this.bannerResizeObserver.observe(adEl);
    }

    private updateBannerDimensions(adEl: HTMLElement) {
        const height = Math.ceil(adEl.getBoundingClientRect().height);
        if (height <= 0) {
            return;
        }

        this.applyBannerLoadedStyles(height);
    }

    private showBannerPlaceholder() {
        this.bannerPlaceholderDisplayedListener?.();
        this.notifyBannerPlaceholderVisibility(true);
        this.applyBannerLoadedStyles(AdsenseProvider.BANNER_PLACEHOLDER_HEIGHT_PX);
    }

    private hideBannerPlaceholder() {
        this.notifyBannerPlaceholderVisibility(false);
    }

    private notifyBannerPlaceholderVisibility(isVisible: boolean, containerId = this.currentBannerContainerId) {
        if (!containerId) {
            return;
        }

        this.bannerPlaceholderVisibilityListener?.(containerId, isVisible);
    }

    private applyBannerLoadedStyles(height: number) {
        if (height > 0) {
            document.documentElement.style.setProperty('--var-web-banner-height', `${height}px`);
        }
        document.body.classList.remove('ad-banner-initializing');
        document.body.classList.add('has-web-ad-banner');
    }

    private removeBannerLoadedStyles() {
        document.documentElement.style.setProperty('--var-web-banner-height', '0px');
        document.body.classList.remove('has-web-ad-banner');
        document.body.classList.remove('ad-banner-initializing');
    }
}
